import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import {
    AgencyMedicalPlan,
    AgencyMedicalPlanRate,
    GpEstimatorBreakdown,
    GpEstimatorInput,
    GpEstimatorResult,
    MedicalPlanImportResult,
} from '../app.model';
import { GpEstimatorService } from '../services/gp-estimator.service';

type ViewMode = 'contract' | 'weekly';
type CardKey = 'rates' | 'medical' | 'expenses';
type Band = 'red' | 'yellow' | 'green';

interface Scenario {
    id: string;
    name: string;
    billRate: number;
    payRate: number;
    gpPct: number;
    band: Band;
    snapshot: any;
}

const HOUR_PRESETS = [20, 30, 40, 48];
const GAUGE_ARC = 361;
const GAUGE_MAX_PCT = 0.45;
const SCENARIO_LIMIT = 2;
const SCENARIO_SLOTS = 3;

@Component({
    selector: 'app-gp-estimator',
    templateUrl: './gp-estimator.component.html',
    styleUrls: ['./gp-estimator.component.css'],
})
export class GpEstimatorComponent implements OnInit, OnDestroy {
    form!: FormGroup;
    result: GpEstimatorResult | null = null;
    calculating = false;
    errorMessage: string | null = null;

    view: ViewMode = 'contract';
    cardsOpen: Record<CardKey, boolean> = { rates: true, medical: true, expenses: false };
    scenarios: Scenario[] = [];
    showBreakdown = false;

    showImportModal = false;
    showViewModal = false;
    loadingPlan = false;
    activePlan: AgencyMedicalPlan | null = null;
    selectedFile: File | null = null;
    importing = false;
    importResult: MedicalPlanImportResult | null = null;
    importError: string | null = null;
    createPlanForm!: FormGroup;
    creatingPlan = false;
    createPlanError: string | null = null;
    viewError: string | null = null;
    editingRateUuid: string | null = null;
    editPremium: number | null = null;
    savingRate = false;
    editError: string | null = null;

    readonly hourPresets = HOUR_PRESETS;
    readonly scenarioLimit = SCENARIO_LIMIT;
    readonly gaugeArc = GAUGE_ARC;

    private destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private gpService: GpEstimatorService,
    ) { }

    ngOnInit(): void {
        this.form = this.fb.group({
            client: [''],
            candidate_name: [''],
            bill_rate: [null, [Validators.required, Validators.min(0)]],
            vms_fee: [0, [Validators.min(0)]],
            pay_rate: [null, [Validators.required, Validators.min(0)]],
            pay_type: ['W2', Validators.required],
            contract_weeks: [null, [Validators.required, Validators.min(0)]],
            hours_per_week: [40, [Validators.required, Validators.min(0), Validators.max(168)]],
            bonus: [0, [Validators.min(0)]],
            medical_participant: [false, Validators.required],
            candidate_age: [null, [Validators.min(0), Validators.max(110)]],
            dependent_ages: this.fb.array([]),
            travel: [0, [Validators.min(0)]],
            licensure: [0, [Validators.min(0)]],
            additional: [0, [Validators.min(0)]],
        });

        this.createPlanForm = this.fb.group({
            name: ['', [Validators.required, Validators.maxLength(255)]],
            effective_from: [this.todayIso(), Validators.required],
            effective_to: [null],
        });

        this.form.valueChanges
            .pipe(
                debounceTime(300),
                takeUntil(this.destroy$),
                switchMap(() => {
                    if (!this.canCalculate()) {
                        this.result = null;
                        return [];
                    }
                    this.calculating = true;
                    this.errorMessage = null;
                    return this.gpService.calculate(this.buildPayload());
                }),
            )
            .subscribe({
                next: (res) => {
                    this.result = res;
                    this.calculating = false;
                },
                error: (err) => {
                    this.calculating = false;
                    this.errorMessage =
                        err?.error?.message || 'Calculation failed. Please check your inputs.';
                },
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get dependentAges(): FormArray {
        return this.form.get('dependent_ages') as FormArray;
    }

    addDependent(): void {
        if (this.dependentAges.length >= 5) return;
        this.dependentAges.push(new FormControl(null, [Validators.min(0), Validators.max(110)]));
    }

    removeDependent(index: number): void {
        this.dependentAges.removeAt(index);
    }

    reset(): void {
        this.dependentAges.clear();
        this.form.reset({
            pay_type: 'W2',
            hours_per_week: 40,
            vms_fee: 0,
            bonus: 0,
            medical_participant: false,
            travel: 0,
            licensure: 0,
            additional: 0,
        });
        this.result = null;
        this.errorMessage = null;
    }

    toggleCard(key: CardKey): void {
        this.cardsOpen[key] = !this.cardsOpen[key];
    }

    setView(view: ViewMode): void {
        this.view = view;
    }

    setHoursPreset(n: number): void {
        this.form.patchValue({ hours_per_week: n });
    }

    isHoursPreset(n: number): boolean {
        return Number(this.form.get('hours_per_week')?.value) === n;
    }

    onBillSlider(ev: Event): void {
        this.form.patchValue({ bill_rate: Number((ev.target as HTMLInputElement).value) });
    }

    onPaySlider(ev: Event): void {
        this.form.patchValue({ pay_rate: Number((ev.target as HTMLInputElement).value) });
    }

    panelBandClass(): string {
        if (!this.result) return '';
        return `band-${this.result.contract.band}`;
    }

    bandLabel(): string {
        if (!this.result) return 'Gross Profit';
        const b = this.result.contract.band;
        return b === 'green' ? 'Healthy' : b === 'yellow' ? 'Marginal' : 'Below target';
    }

    gaugeOffset(): number {
        if (!this.result) return GAUGE_ARC;
        const frac = Math.max(0, Math.min(1, this.result.contract.gp_pct / GAUGE_MAX_PCT));
        return GAUGE_ARC * (1 - frac);
    }

    currentBreakdown(): GpEstimatorBreakdown | null {
        if (!this.result) return null;
        return this.view === 'contract' ? this.result.contract : this.result.weekly;
    }

    expenseBarWidth(amount: number): number {
        const rev = this.currentBreakdown()?.revenue ?? 0;
        if (!rev || amount <= 0) return 0;
        return Math.max(1, Math.min(100, (amount / rev) * 100));
    }

    gpBarWidth(): number {
        const b = this.currentBreakdown();
        if (!b || b.revenue <= 0 || b.gross_profit <= 0) return 2;
        return Math.max(2, (b.gross_profit / b.revenue) * 100);
    }

    saveScenario(): void {
        if (!this.result || this.scenarios.length >= SCENARIO_LIMIT) return;
        const v = this.form.value;
        this.scenarios.push({
            id: String(Date.now()),
            name: `Scenario ${String.fromCharCode(65 + this.scenarios.length)}`,
            billRate: Number(v.bill_rate),
            payRate: Number(v.pay_rate),
            gpPct: this.result.contract.gp_pct,
            band: this.result.contract.band,
            snapshot: { ...v, dependent_ages: [...(v.dependent_ages || [])] },
        });
    }

    applyScenario(s: Scenario): void {
        const { dependent_ages, ...rest } = s.snapshot;
        this.dependentAges.clear();
        (dependent_ages || []).forEach((a: number) =>
            this.dependentAges.push(new FormControl(a, [Validators.min(0), Validators.max(110)])),
        );
        this.form.patchValue(rest);
    }

    removeScenario(id: string, ev: Event): void {
        ev.stopPropagation();
        this.scenarios = this.scenarios.filter((s) => s.id !== id);
    }

    emptySlots(): null[] {
        const used = this.scenarios.length + (this.result ? 1 : 0);
        return new Array(Math.max(0, SCENARIO_SLOTS - used)).fill(null);
    }

    private canCalculate(): boolean {
        if (this.form.invalid) return false;
        const v = this.form.value;
        return (
            v.bill_rate !== null &&
            v.pay_rate !== null &&
            v.contract_weeks !== null &&
            v.hours_per_week !== null
        );
    }

    openImportModal(): void {
        this.showImportModal = true;
        this.selectedFile = null;
        this.importResult = null;
        this.importError = null;
        this.createPlanError = null;
        this.loadActivePlan();
    }

    openViewModal(): void {
        this.showViewModal = true;
        this.viewError = null;
        this.loadActivePlan();
    }

    closeModals(): void {
        this.showImportModal = false;
        this.showViewModal = false;
        this.cancelEditRate();
    }

    onFileChange(ev: Event): void {
        const input = ev.target as HTMLInputElement;
        this.selectedFile = input.files && input.files.length ? input.files[0] : null;
        this.importResult = null;
        this.importError = null;
    }

    submitImport(): void {
        if (!this.activePlan || !this.selectedFile || this.importing) return;
        this.importing = true;
        this.importError = null;
        this.importResult = null;

        this.gpService.importRates(this.activePlan.uuid, this.selectedFile).subscribe({
            next: (res) => {
                this.importing = false;
                this.importResult = res;
                this.selectedFile = null;
            },
            error: (err) => {
                this.importing = false;
                this.importError = err?.error?.message || 'Failed to import rates.';
            },
        });
    }

    submitCreatePlan(): void {
        if (this.createPlanForm.invalid || this.creatingPlan) return;
        this.creatingPlan = true;
        this.createPlanError = null;

        const v = this.createPlanForm.value;
        this.gpService
            .createPlan({
                name: v.name,
                effective_from: v.effective_from,
                effective_to: v.effective_to || null,
                is_active: true,
            })
            .subscribe({
                next: (res) => {
                    this.creatingPlan = false;
                    this.activePlan = res.plan;
                },
                error: (err) => {
                    this.creatingPlan = false;
                    this.createPlanError = err?.error?.message || 'Failed to create plan.';
                },
            });
    }

    startEditRate(rate: AgencyMedicalPlanRate): void {
        if (this.editingRateUuid) return;
        this.editingRateUuid = rate.uuid;
        this.editPremium = Number(rate.monthly_premium);
        this.editError = null;
    }

    cancelEditRate(): void {
        this.editingRateUuid = null;
        this.editPremium = null;
        this.savingRate = false;
        this.editError = null;
    }

    isEditPremiumValid(): boolean {
        return this.editPremium !== null && !isNaN(this.editPremium) && this.editPremium >= 0;
    }

    saveEditRate(): void {
        if (!this.activePlan || !this.editingRateUuid || !this.isEditPremiumValid() || this.savingRate) return;

        const rateUuid = this.editingRateUuid;
        const premium = Number(this.editPremium);
        this.savingRate = true;
        this.editError = null;

        this.gpService.updateRate(this.activePlan.uuid, rateUuid, premium).subscribe({
            next: (res) => {
                if (this.activePlan?.rates) {
                    const idx = this.activePlan.rates.findIndex((r) => r.uuid === rateUuid);
                    if (idx >= 0) {
                        this.activePlan.rates[idx] = res.rate;
                    }
                }
                this.cancelEditRate();
            },
            error: (err) => {
                this.savingRate = false;
                this.editError = err?.error?.message || 'Failed to update rate.';
            },
        });
    }

    private loadActivePlan(): void {
        this.loadingPlan = true;
        this.activePlan = null;
        this.gpService.getActivePlan().subscribe({
            next: (res) => {
                this.loadingPlan = false;
                this.activePlan = res.plan;
            },
            error: (err) => {
                this.loadingPlan = false;
                const msg = err?.error?.message || 'Failed to load active medical plan.';
                this.importError = msg;
                this.viewError = msg;
            },
        });
    }

    private todayIso(): string {
        const d = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }

    private buildPayload(): GpEstimatorInput {
        const v = this.form.value;
        const ages = (v.dependent_ages || []).filter(
            (a: number | null) => a !== null && a !== undefined,
        );
        return {
            client: v.client || undefined,
            candidate_name: v.candidate_name || undefined,
            bill_rate: Number(v.bill_rate),
            vms_fee: Number(v.vms_fee || 0),
            pay_rate: Number(v.pay_rate),
            pay_type: v.pay_type,
            contract_weeks: Number(v.contract_weeks),
            hours_per_week: Number(v.hours_per_week),
            bonus: Number(v.bonus || 0),
            medical_participant: !!v.medical_participant,
            candidate_age: v.medical_participant ? Number(v.candidate_age) : null,
            dependent_ages: ages.map((a: number) => Number(a)),
            travel: Number(v.travel || 0),
            licensure: Number(v.licensure || 0),
            additional: Number(v.additional || 0),
        };
    }
}